import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit,
    faTrash,
    faEye,
    faSave,
    faSearch,
    faRefresh,
    faBoxes,
    faSpinner,
    faExclamationTriangle,
    faCheckCircle,
    faTimes,
    faChevronLeft,
    faChevronRight,
    faFilter,
    faCalendarAlt,
    faFilePdf,
    faPrint,
    faDownload,
    faCalendar,
    faTimesCircle,
    faBox,
    faTag,
    faBuilding,
    faBalanceScale,
    faDollarSign,
    faCalculator,
    faExclamationCircle,
    faHashtag,
    faPlus,
    faBarcode,
    faLayerGroup,
    faIndustry,
    faWeightHanging,
    faWarehouse,
    faFileExport,
    faChartLine,
    faPercent,
    faUser,
    faPalette,
    faRulerCombined,
    faBoxes as faBoxesIcon,
    faInfoCircle,
    faHistory,
    faStore,
    faExchangeAlt,
    faShoppingCart,
    faTruck,
    faShip,
    faRoad,
    faMoneyBillWave,
    faUniversity,
    faMoneyCheck,
    faCreditCard,
    faPhone,
    faEnvelope,
    faMapMarkerAlt,
    faGlobe,
    faClock,
    faCalendarCheck,
    faFileInvoice,
    faReceipt,
    faMoneyBill,
    faDownload as faDownloadIcon,
    faHourglassHalf,
    faCheckDouble,
    faMapMarkedAlt,
    faFlag,
    faClock as faClockIcon,
    faIdCard,
    faAddressCard,
    faComment,
    faMoneyCheckAlt,
    faBan,
    faBell,
    faMinus,
    faArrowsAlt,
    faExpand,
    faCompress
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas'; // FIX: Added missing import
import "../styles/CustomerList.css";
import "../styles/Customer.css";

// Create translator instance for cheque list
const createChequeListTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_cheque_list_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Cheque List");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_cheque_list_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        chequeList: 'Cheques List',
        allCheques: 'My Cheques',
        cheques: 'Cheques',
        shopName: 'Shop',
        shopCode: 'Shop Code',
        currentShop: 'Current Shop',
        noShopSelected: 'No shop selected. Please select a shop first.',
        switchShop: 'Switch Shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        current: 'Current',
        searchPlaceholder: 'Search cheques...',
        refresh: 'Refresh',
        loadingCheques: 'Loading cheques...',
        noChequesFound: 'No cheques found',
        chequeDetails: 'Cheque Details',
        close: 'Close',
        serialNo: 'S.No',
        id: 'ID',
        chequeNo: 'Cheque No',
        bankName: 'Bank Name',
        payeeName: 'Payee Name',
        amount: 'Amount',
        issueDate: 'Issue Date',
        chequeDate: 'Cheque Date',
        depositDate: 'Deposit Date',
        status: 'Status',
        remarks: 'Remarks',
        actions: 'Actions',
        addedBy: 'Added By',
        shopInfo: 'Shop',
        currency: 'Currency',
        issued: 'Issued',
        pending: 'Pending',
        deposited: 'Deposited',
        cleared: 'Cleared',
        bounced: 'Bounced',
        cancelled: 'Cancelled',
        stopped: 'Stopped',
        expired: 'Expired',
        view: 'View',
        edit: 'Edit',
        delete: 'Delete',
        deleteConfirm: 'Are you sure you want to delete this cheque?',
        deletedSuccess: 'Cheque deleted successfully',
        deleteError: 'Error deleting cheque',
        networkError: 'Network error occurred',
        chequeId: 'Cheque ID',
        bankDetails: 'Bank Details',
        drawerInfo: 'Drawer Information',
        timelineInfo: 'Timeline Information',
        notProvided: 'Not provided',
        amountInWords: 'Amount in Words',
        showing: 'Showing',
        of: 'of',
        previous: 'Previous',
        next: 'Next',
        filter: 'Filter',
        all: 'All',
        filterBy: 'Filter By',
        selectFilter: 'Select Filter',
        chequeNoFilter: 'Cheque Number',
        bankNameFilter: 'Bank Name',
        payeeNameFilter: 'Payee Name',
        statusFilter: 'Status',
        dateRange: 'Date Range',
        startDate: 'Start Date',
        endDate: 'End Date',
        applyFilters: 'Apply Filters',
        resetFilters: 'Reset Filters',
        exportToPDF: 'Export PDF',
        addedByFilter: 'Added By',
        minAmount: 'Min Amount',
        maxAmount: 'Max Amount',
        editCheque: 'Edit Cheque',
        updateCheque: 'Update Cheque',
        updating: 'Updating...',
        chequeUpdated: 'Cheque updated successfully',
        updateError: 'Error updating cheque',
        cancel: 'Cancel',
        saveChanges: 'Save Changes',
        fillDetails: 'Fill in the cheque details below',
        chequeNoRequired: 'Cheque number is required',
        invalidAmount: 'Please enter a valid amount',
        confirmDelete: 'Confirm Delete',
        deleteMessage: 'Are you sure you want to delete this cheque?',
        deleteWarning: 'This action cannot be undone.',
        yesDelete: 'Yes, Delete',
        noCancel: 'No, Cancel',
        myRecords: 'My Records Only',
        loadingUserData: 'Loading your cheque data...',
        noUserRecords: 'No cheques found in this shop',
        businessReport: 'Business Report',
        generatedBy: 'Generated by',
        chequeReport: 'Cheque Report',
        chequeSummary: 'Cheque Summary',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        totalCheques: 'Total Cheques',
        totalAmount: 'Total Amount',
        avgAmount: 'Average Amount',
        bouncedCheques: 'Bounced Cheques',
        clearedCheques: 'Cleared Cheques',
        pendingCheques: 'Pending Cheques',
        dateAdded: 'Date Added',
        lastUpdated: 'Last Updated',
        generatingPDF: 'Generating PDF...',
        pdfGenerationError: 'Error generating PDF',
        pdfGenerated: 'PDF generated successfully',
        reportFilters: 'Applied Filters',
        reportDate: 'Report Date',
        createFirstCheque: 'Create Your First Cheque',
        addNewCheque: 'Add New Cheque',
        cashableReminder: 'Cashable in Next 7 Days',
        daysRemaining: 'days remaining',
        chequeDue: 'Cheque Due',
        cashableOn: 'Cashable on',
        reminderTitle: 'Cheque Cashable Reminder',
        bankBranch: 'Bank Branch',
        bankCode: 'Bank Code',
        accountNumber: 'Account Number',
        drawerName: 'Drawer Name',
        drawerPhone: 'Drawer Phone',
        bounceReason: 'Bounce Reason',
        bounceDate: 'Bounce Date'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const chequeListTranslator = createChequeListTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationTriangle;
            case 'warning':
                return faExclamationTriangle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return translations.success || 'Success';
            case 'error':
                return translations.error || 'Error';
            case 'warning':
                return translations.warning || 'Warning';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
            style={{ opacity }}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div
                    className="toast-progress-bar"
                    style={{
                        width: `${toast.progress}%`,
                        transition: 'width 0.1s linear'
                    }}
                ></div>
                <div className="toast-countdown">
                    {toast.timeLeft}s
                </div>
            </div>
        </div>
    );
};

// Shop Selector Modal Component
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    const handleShopSelect = (shop) => {
        onSelectShop(shop);
        onClose();
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content shop-selector-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="modal-body">
                    {loading ? (
                        <div className="loading-shops">
                            <div className="loading-spinner-small"></div>
                            <p>{translations.loadingShops || 'Loading shops...'}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="no-shops">
                            <p>{translations.noShops || 'No shops available'}</p>
                        </div>
                    ) : (
                        <div className="shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`shop-item ${currentShop?.shop_id === shop.shop_id ? 'active' : ''}`}
                                    onClick={() => handleShopSelect(shop)}
                                >
                                    <div className="shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="shop-item-details">
                                        <div className="shop-item-name">{shop.shop_name}</div>
                                        <div className="shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                        {shop.shop_role && (
                                            <div className="shop-item-role">Role: {shop.shop_role}</div>
                                        )}
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{translations.current || 'Current'}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Status Badge Component
const StatusBadge = ({ status, translations }) => {
    const getStatusStyle = (status) => {
        switch (status) {
            case 'issued':
                return { bg: '#f59e0b20', color: '#f59e0b', border: '#f59e0b40', icon: faMoneyCheck };
            case 'pending':
                return { bg: '#3b82f620', color: '#3b82f6', border: '#3b82f640', icon: faClock };
            case 'deposited':
                return { bg: '#8b5cf620', color: '#8b5cf6', border: '#8b5cf640', icon: faUniversity };
            case 'cleared':
                return { bg: '#10b98120', color: '#10b981', border: '#10b98140', icon: faCheckCircle };
            case 'bounced':
                return { bg: '#ef444420', color: '#ef4444', border: '#ef444440', icon: faExclamationTriangle };
            case 'cancelled':
                return { bg: '#6b728020', color: '#6b7280', border: '#6b728040', icon: faTimesCircle };
            case 'stopped':
                return { bg: '#ef444420', color: '#ef4444', border: '#ef444440', icon: faBan };
            case 'expired':
                return { bg: '#6b728020', color: '#6b7280', border: '#6b728040', icon: faHourglassHalf };
            default:
                return { bg: '#6b728020', color: '#6b7280', border: '#6b728040', icon: faMoneyCheck };
        }
    };

    const style = getStatusStyle(status);
    const label = translations?.[status] || status;

    return (
        <div className="status-badge" style={{
            backgroundColor: style.bg,
            color: style.color,
            border: `1px solid ${style.border}`,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            padding: '4px 8px',
            borderRadius: '20px',
            fontSize: '12px'
        }}>
            <FontAwesomeIcon icon={style.icon} size="xs" />
            {label}
        </div>
    );
};

// Cashable Reminder Component
const CashableReminder = ({ cheques, translations }) => {
    const [showReminder, setShowReminder] = useState(true);
    
    const getCashableCheques = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const sevenDaysFromNow = new Date(today);
        sevenDaysFromNow.setDate(today.getDate() + 7);
        
        return cheques.filter(cheque => {
            if (!cheque.cheque_date) return false;
            const status = cheque.status;
            if (status === 'cleared' || status === 'bounced' || status === 'cancelled' || status === 'stopped') {
                return false;
            }
            const chequeDate = new Date(cheque.cheque_date);
            chequeDate.setHours(0, 0, 0, 0);
            return chequeDate >= today && chequeDate <= sevenDaysFromNow;
        });
    };
    
    const cashableCheques = getCashableCheques();
    
    if (cashableCheques.length === 0 || !showReminder) return null;
    
    const getDaysRemaining = (chequeDate) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const date = new Date(chequeDate);
        date.setHours(0, 0, 0, 0);
        const diffTime = date - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };
    
    return (
        <div className="cashable-reminder-container" style={{
            background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
            border: '1px solid #f59e0b',
            borderRadius: '12px',
            padding: '15px 20px',
            margin: '20px 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '15px',
            position: 'relative'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                <div style={{
                    background: '#f59e0b',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <FontAwesomeIcon icon={faBell} style={{ color: 'white', fontSize: '20px' }} />
                </div>
                <div>
                    <h4 style={{ margin: 0, color: '#92400e', fontSize: '16px', fontWeight: 'bold' }}>
                        {translations.reminderTitle || 'Cheque Cashable Reminder'}
                    </h4>
                    <p style={{ margin: '5px 0 0', color: '#78350f', fontSize: '14px' }}>
                        {cashableCheques.length} {translations.cheques || 'cheque(s)'} {translations.cashableReminder || 'cashable in the next 7 days'}
                    </p>
                </div>
            </div>
            
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {cashableCheques.slice(0, 3).map((cheque, index) => {
                    const daysRemaining = getDaysRemaining(cheque.cheque_date);
                    return (
                        <div key={index} style={{
                            background: 'white',
                            borderRadius: '8px',
                            padding: '8px 12px',
                            minWidth: '150px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }}>
                            <div style={{ fontSize: '12px', color: '#666' }}>#{cheque.cheque_no}</div>
                            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#92400e' }}>
                                {cheque.currency_symbol || '$'} {parseFloat(cheque.amount || 0).toFixed(2)}
                            </div>
                            <div style={{ fontSize: '11px', color: '#f59e0b' }}>
                                {translations.cashableOn || 'Cashable on'}: {new Date(cheque.cheque_date).toLocaleDateString()}
                                {' '}({daysRemaining} {translations.daysRemaining || 'days remaining'})
                            </div>
                        </div>
                    );
                })}
                {cashableCheques.length > 3 && (
                    <div style={{
                        background: '#fef3c7',
                        borderRadius: '8px',
                        padding: '8px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#92400e',
                        fontSize: '14px'
                    }}>
                        +{cashableCheques.length - 3} more
                    </div>
                )}
            </div>
            
            <button
                onClick={() => setShowReminder(false)}
                style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#92400e',
                    cursor: 'pointer',
                    fontSize: '18px',
                    padding: '5px'
                }}
            >
                <FontAwesomeIcon icon={faTimes} />
            </button>
        </div>
    );
};

// Width modes for modal
const widthModes = {
    narrow: { width: 450, label: 'Narrow', icon: faMinus },
    medium: { width: 750, label: 'Medium', icon: faArrowsAlt },
    wide: { width: 950, label: 'Wide', icon: faPlus },
    full: { width: '95vw', label: 'Full Width', icon: faExpand },
    fullscreen: { width: '98vw', label: 'Full Screen', icon: faCompress }
};

// Main Component
const ChequeList = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    const [cheques, setCheques] = useState([]);
    const [filteredCheques, setFilteredCheques] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCheque, setSelectedCheque] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [chequeToDelete, setChequeToDelete] = useState(null);
    const [editLoading, setEditLoading] = useState(false);

    // Modal drag and resize state
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
    const [modalSize, setModalSize] = useState({ width: 750, height: 'auto' });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [currentWidthMode, setCurrentWidthMode] = useState('medium');
    const [isModalInitialized, setIsModalInitialized] = useState(false);

    const dragStart = useRef({ x: 0, y: 0 });
    const modalRef = useRef(null);
    const resizeType = useRef(null);
    const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0, left: 0, top: 0 });

    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr',
        progress: 100,
        timeLeft: 3
    });

    // Timer reference for cleanup
    const toastTimerRef = useRef(null);

    // Edit form state
    const [editFormData, setEditFormData] = useState({
        cheque_no: '',
        bank_name: '',
        payee_name: '',
        amount: '',
        currency_code: 'USD',
        currency_symbol: '$',
        issue_date: '',
        cheque_date: '',
        deposit_date: '',
        status: '',
        remarks: '',
        bank_branch: '',
        bank_code: '',
        account_number: '',
        drawer_name: '',
        drawer_phone: '',
        bounce_reason: '',
        bounce_date: ''
    });
    const [editFormErrors, setEditFormErrors] = useState({});

    // Business info state
    const [businessInfo, setBusinessInfo] = useState({
        businessname: '',
        bussinesslogo: '',
        businessaddress: '',
        fullname: '',
        email: ''
    });

    // Get theme and language from localStorage
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [currentTheme, setCurrentTheme] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#8b5cf6';
    });

    const [translations, setTranslations] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Enhanced filter states
    const [filterBy, setFilterBy] = useState('all');
    const [chequeNoFilter, setChequeNoFilter] = useState('');
    const [bankNameFilter, setBankNameFilter] = useState('');
    const [payeeNameFilter, setPayeeNameFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [currencyFilter, setCurrencyFilter] = useState('');
    const [addedByFilter, setAddedByFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [minAmount, setMinAmount] = useState('');
    const [maxAmount, setMaxAmount] = useState('');

    // Refs for date inputs
    const startDateRef = useRef(null);
    const endDateRef = useRef(null);

    // Status options for dropdown
    const statusOptions = [
        { value: '', label: 'All Status' },
        { value: 'issued', label: 'Issued' },
        { value: 'pending', label: 'Pending' },
        { value: 'deposited', label: 'Deposited' },
        { value: 'cleared', label: 'Cleared' },
        { value: 'bounced', label: 'Bounced' },
        { value: 'cancelled', label: 'Cancelled' },
        { value: 'stopped', label: 'Stopped' },
        { value: 'expired', label: 'Expired' }
    ];

    // Currency options
    const currencyOptions = [
        { value: '', label: 'All Currencies' },
        { value: 'USD', label: 'USD ($)' },
        { value: 'EUR', label: 'EUR (€)' },
        { value: 'GBP', label: 'GBP (£)' },
        { value: 'JPY', label: 'JPY (¥)' },
        { value: 'CNY', label: 'CNY (¥)' },
        { value: 'INR', label: 'INR (₹)' },
        { value: 'PKR', label: 'PKR (₨)' },
        { value: 'AED', label: 'AED (د.إ)' },
        { value: 'SAR', label: 'SAR (﷼)' },
        { value: 'CAD', label: 'CAD (C$)' },
        { value: 'AUD', label: 'AUD (A$)' },
        { value: 'CHF', label: 'CHF (Fr)' },
        { value: 'SGD', label: 'SGD (S$)' }
    ];

    // Calculate totals
    const totalCheques = filteredCheques.length;
    const totalAmount = filteredCheques.reduce((sum, cheque) => sum + (parseFloat(cheque.amount) || 0), 0);
    const avgAmount = totalCheques > 0 ? totalAmount / totalCheques : 0;
    
    const bouncedCount = filteredCheques.filter(c => c.status === 'bounced').length;
    const clearedCount = filteredCheques.filter(c => c.status === 'cleared').length;
    const pendingCount = filteredCheques.filter(c => c.status === 'pending' || c.status === 'issued').length;

    const totalPages = Math.ceil(filteredCheques.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentCheques = filteredCheques.slice(startIndex, endIndex);

    // Center modal function
    const centerModal = useCallback(() => {
        if (!modalRef.current) return;
        
        const rect = modalRef.current.getBoundingClientRect();
        const left = (window.innerWidth - rect.width) / 2;
        const top = (window.innerHeight - rect.height) / 2;
        
        modalRef.current.style.left = `${Math.max(0, left)}px`;
        modalRef.current.style.top = `${Math.max(0, top)}px`;
        modalRef.current.style.transform = 'none';
        
        setModalPosition({ x: Math.max(0, left), y: Math.max(0, top) });
    }, []);

    // Set width mode
    const setWidthMode = useCallback((mode) => {
        if (!modalRef.current) return;
        
        setCurrentWidthMode(mode);
        const widthValue = widthModes[mode].width;
        
        if (mode === 'fullscreen') {
            modalRef.current.style.width = '98vw';
            modalRef.current.style.height = '90vh';
            modalRef.current.style.maxWidth = '98vw';
            modalRef.current.style.maxHeight = '90vh';
        } else {
            modalRef.current.style.width = typeof widthValue === 'number' ? `${widthValue}px` : widthValue;
            modalRef.current.style.height = 'auto';
            modalRef.current.style.maxWidth = '95vw';
            modalRef.current.style.maxHeight = '85vh';
        }
        
        setModalSize({ width: modalRef.current.offsetWidth, height: modalRef.current.offsetHeight });
        
        setTimeout(() => centerModal(), 10);
    }, [centerModal]);

    // Start dragging
    const startDrag = useCallback((e) => {
        if (!modalRef.current) return;
        
        if (e.target.closest('button, input, select, textarea, a, .width-btn, .close-btn-modern')) {
            return;
        }
        
        e.preventDefault();
        setIsDragging(true);
        
        const rect = modalRef.current.getBoundingClientRect();
        dragStart.current = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        };
        
        modalRef.current.style.transition = 'none';
        modalRef.current.classList.add('dragging');
    }, []);

    // Start resizing
    const startResize = useCallback((e, type) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!modalRef.current) return;
        
        setIsResizing(true);
        resizeType.current = type;
        
        const rect = modalRef.current.getBoundingClientRect();
        
        resizeStart.current = {
            x: e.clientX,
            y: e.clientY,
            width: rect.width,
            height: rect.height,
            left: rect.left,
            top: rect.top,
            minWidth: 400,
            minHeight: 500,
            maxWidth: window.innerWidth - 40,
            maxHeight: window.innerHeight - 40
        };
        
        modalRef.current.style.transition = 'none';
        modalRef.current.classList.add('resizing');
    }, []);

    // Handle drag and resize movement
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging && modalRef.current) {
                let newLeft = e.clientX - dragStart.current.x;
                let newTop = e.clientY - dragStart.current.y;
                
                const rect = modalRef.current.getBoundingClientRect();
                newLeft = Math.max(0, Math.min(newLeft, window.innerWidth - rect.width));
                newTop = Math.max(0, Math.min(newTop, window.innerHeight - rect.height));
                
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                modalRef.current.style.transform = 'none';
                
                setModalPosition({ x: newLeft, y: newTop });
            }
            
            if (isResizing && modalRef.current && resizeType.current) {
                let newWidth = resizeStart.current.width;
                let newHeight = resizeStart.current.height;
                let newLeft = resizeStart.current.left;
                let newTop = resizeStart.current.top;
                
                const deltaX = e.clientX - resizeStart.current.x;
                const deltaY = e.clientY - resizeStart.current.y;
                
                switch (resizeType.current) {
                    case 'e':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX));
                        break;
                    case 'w':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX));
                        newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth);
                        break;
                    case 's':
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY));
                        break;
                    case 'n':
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY));
                        newTop = resizeStart.current.top + (resizeStart.current.height - newHeight);
                        break;
                    case 'ne':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX));
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY));
                        newTop = resizeStart.current.top + (resizeStart.current.height - newHeight);
                        break;
                    case 'nw':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX));
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY));
                        newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth);
                        newTop = resizeStart.current.top + (resizeStart.current.height - newHeight);
                        break;
                    case 'se':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX));
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY));
                        break;
                    case 'sw':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX));
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY));
                        newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth);
                        break;
                }
                
                modalRef.current.style.width = `${newWidth}px`;
                modalRef.current.style.height = `${newHeight}px`;
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                modalRef.current.style.transform = 'none';
                
                setModalSize({ width: newWidth, height: newHeight });
                setModalPosition({ x: newLeft, y: newTop });
            }
        };
        
        const handleMouseUp = () => {
            if (isDragging || isResizing) {
                setIsDragging(false);
                setIsResizing(false);
                resizeType.current = null;
                
                if (modalRef.current) {
                    modalRef.current.style.transition = '';
                    modalRef.current.classList.remove('dragging', 'resizing');
                }
            }
        };
        
        if (isDragging || isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.classList.add('dragging-active');
        }
        
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.classList.remove('dragging-active');
        };
    }, [isDragging, isResizing]);

    // Initialize modal position when opened
    useEffect(() => {
        if (showEditModal && modalRef.current && !isModalInitialized) {
            setTimeout(() => {
                centerModal();
                setWidthMode('medium');
                setIsModalInitialized(true);
            }, 50);
        }
        
        if (!showEditModal) {
            setIsModalInitialized(false);
        }
    }, [showEditModal, centerModal, setWidthMode, isModalInitialized]);

    // Reset position on window resize
    useEffect(() => {
        const handleWindowResize = () => {
            if (!modalRef.current || !showEditModal) return;
            
            const rect = modalRef.current.getBoundingClientRect();
            let newLeft = Math.min(rect.left, window.innerWidth - rect.width);
            let newTop = Math.min(rect.top, window.innerHeight - rect.height);
            newLeft = Math.max(0, newLeft);
            newTop = Math.max(0, newTop);
            
            if (newLeft !== rect.left || newTop !== rect.top) {
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                setModalPosition({ x: newLeft, y: newTop });
            }
        };
        
        window.addEventListener('resize', handleWindowResize);
        return () => window.removeEventListener('resize', handleWindowResize);
    }, [showEditModal]);

    // Function to show toast
    const showToast = (type, message) => {
        if (toastTimerRef.current) {
            clearInterval(toastTimerRef.current);
        }

        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';

        setToast({
            show: true,
            type,
            message,
            direction,
            progress: 100,
            timeLeft: 3
        });

        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;

            setToast(prev => ({
                ...prev,
                progress,
                timeLeft: parseFloat(timeLeft.toFixed(1))
            }));

            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);

        toastTimerRef.current = timer;
    };

    // Clean up timer on component unmount
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                clearInterval(toastTimerRef.current);
            }
        };
    }, []);

    // Apply theme
    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#8b5cf6';

                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);

                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };

                const palette = {
                    'customer-primary-color': color,
                    'customer-primary-dark': adjust(-40),
                    'customer-primary-light': adjust(40),
                    'customer-primary-ultralight': adjust(20),
                    'customer-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'customer-primary-shadow': `${color}20`
                };

                Object.entries(palette).forEach(([key, value]) => {
                    document.documentElement.style.setProperty(`--${key}`, value);
                });

                document.documentElement.style.setProperty('--primary-color', color);
                document.documentElement.style.setProperty('--primary-dark', adjust(-40));
                document.documentElement.style.setProperty('--primary-light', adjust(40));
                document.documentElement.style.setProperty('--primary-ultralight', adjust(20));
                document.documentElement.style.setProperty('--primary-gradient', `linear-gradient(135deg, ${color}, ${adjust(-20)})`);
                document.documentElement.style.setProperty('--primary-shadow', `${color}20`);
                document.documentElement.style.setProperty('--row-active', adjust(10));

            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };

        applyTheme(currentTheme);

        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#8b5cf6';
            if (theme !== currentTheme) {
                setCurrentTheme(theme);
                applyTheme(theme);
            }
        };

        handleThemeChange();

        window.addEventListener('storage', handleThemeChange);

        const interval = setInterval(handleThemeChange, 1000);

        return () => {
            window.removeEventListener('storage', handleThemeChange);
            clearInterval(interval);
        };
    }, [currentTheme]);

    // Load translations
    useEffect(() => {
        const loadTranslations = async () => {
            const trans = await chequeListTranslator.getTranslations(currentLanguage);
            setTranslations(trans);
        };
        loadTranslations();
    }, [currentLanguage]);

    // Listen for language changes
    useEffect(() => {
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
            }
        };

        handleLanguageChange();

        window.addEventListener('storage', handleLanguageChange);

        const interval = setInterval(handleLanguageChange, 1000);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    // Apply RTL direction
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
            document.documentElement.style.direction = 'rtl';
            document.documentElement.lang = currentLanguage;
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
            document.documentElement.lang = currentLanguage;
        }

        return () => {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
        };
    }, [currentLanguage]);

    // Fetch cheques on mount and when shop changes
    useEffect(() => {
        if (user && user.username) {
            fetchCheques();
            fetchBusinessInfo();
            fetchAvailableShops();
        } else {
            setLoading(false);
            showToast('error', 'User not authenticated. Please login again.');
        }
    }, [user, currentShop]);

    // Apply filters
    useEffect(() => {
        applyFilters();
    }, [cheques, searchTerm, filterBy, chequeNoFilter, bankNameFilter, payeeNameFilter, 
        statusFilter, currencyFilter, addedByFilter, startDate, endDate, minAmount, maxAmount]);

    // Fetch available shops for switching
    const fetchAvailableShops = async () => {
        if (!user) return;

        setLoadingShops(true);
        try {
            const shopsData = await getShopsForSwitch();
            setAvailableShops(shopsData.shops || []);
        } catch (error) {
            console.error('Error loading shops:', error);
            setAvailableShops([]);
        } finally {
            setLoadingShops(false);
        }
    };

    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(
                shop.shop_id,
                shop.shop_name,
                shop.shop_code
            );

            if (success) {
                showToast('success', 'Shop switched successfully');
                fetchCheques();
            } else {
                showToast('error', 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', 'Failed to switch shop');
        }
    };

    // Render shop indicator
    const renderShopIndicator = () => {
        if (!currentShop) {
            return (
                <div className="shop-indicator-modern no-shop">
                    <div className="shop-badge">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span className="shop-warning">
                            {translations.noShopSelected || 'No shop selected'}
                        </span>
                    </div>
                    <button
                        className="btn-modern btn-switch-shop"
                        onClick={() => setShowShopSelector(true)}
                    >
                        <FontAwesomeIcon icon={faStore} />
                        <span>{translations.switchShop || 'Switch Shop'}</span>
                    </button>
                </div>
            );
        }
        return null;
    };

    // Fetch business info
    const fetchBusinessInfo = async () => {
        if (!user || !user.username) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/get_business_info.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_business_info',
                    username: user.username
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setBusinessInfo({
                    businessname: data.businessname || '',
                    bussinesslogo: data.bussinesslogo || '',
                    businessaddress: data.businessaddress || '',
                    fullname: data.fullname || '',
                    email: data.email || ''
                });
            } else {
                setBusinessInfo({
                    businessname: user.business_name || '',
                    bussinesslogo: '',
                    businessaddress: '',
                    fullname: user.fullname || '',
                    email: user.email || ''
                });
            }
        } catch (error) {
            console.error('Error fetching business info:', error);
            setBusinessInfo({
                businessname: user.business_name || '',
                bussinesslogo: '',
                businessaddress: '',
                fullname: user.fullname || '',
                email: user.email || ''
            });
        }
    };

    const fetchCheques = async () => {
        if (!user || !user.username) {
            showToast('error', 'User information not available. Please login again.');
            setLoading(false);
            return;
        }

        if (!currentShop) {
            showToast('error', translations.noShopSelected || 'Please select a shop first');
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/cheques.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_records',
                    username: user.username,
                    shop_id: currentShop.shop_id,
                    filters: {}
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setCheques(data.data || []);
                resetFilters();

                if (data.data.length === 0) {
                    showToast('info', translations.noUserRecords || 'No cheques found in this shop');
                }
            } else {
                if (data.message.includes('not assigned to any shop')) {
                    showToast('error', 'You are not assigned to any shop. Please contact administrator.');
                } else {
                    showToast('error', data.message || translations.networkError || 'Network error occurred');
                }
            }
        } catch (error) {
            console.error('Error fetching cheques:', error);
            showToast('error', translations.networkError || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...cheques];

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(cheque =>
                cheque.cheque_no?.toLowerCase().includes(term) ||
                cheque.bank_name?.toLowerCase().includes(term) ||
                cheque.payee_name?.toLowerCase().includes(term) ||
                cheque.drawer_name?.toLowerCase().includes(term) ||
                cheque.remarks?.toLowerCase().includes(term) ||
                cheque.created_by_username?.toLowerCase().includes(term)
            );
        }

        if (filterBy === 'chequeNo' && chequeNoFilter.trim()) {
            const term = chequeNoFilter.toLowerCase();
            filtered = filtered.filter(cheque =>
                cheque.cheque_no?.toLowerCase().includes(term)
            );
        }

        if (filterBy === 'bankName' && bankNameFilter.trim()) {
            const term = bankNameFilter.toLowerCase();
            filtered = filtered.filter(cheque =>
                cheque.bank_name?.toLowerCase().includes(term)
            );
        }

        if (filterBy === 'payeeName' && payeeNameFilter.trim()) {
            const term = payeeNameFilter.toLowerCase();
            filtered = filtered.filter(cheque =>
                cheque.payee_name?.toLowerCase().includes(term)
            );
        }

        if (filterBy === 'status' && statusFilter) {
            filtered = filtered.filter(cheque =>
                cheque.status === statusFilter
            );
        }

        if (filterBy === 'currency' && currencyFilter) {
            filtered = filtered.filter(cheque =>
                cheque.currency_code === currencyFilter
            );
        }

        if (filterBy === 'added_by' && addedByFilter.trim()) {
            const term = addedByFilter.toLowerCase();
            filtered = filtered.filter(cheque =>
                cheque.created_by_username?.toLowerCase().includes(term)
            );
        }

        if (minAmount) {
            filtered = filtered.filter(cheque =>
                parseFloat(cheque.amount) >= parseFloat(minAmount)
            );
        }

        if (maxAmount) {
            filtered = filtered.filter(cheque =>
                parseFloat(cheque.amount) <= parseFloat(maxAmount)
            );
        }

        if (startDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            filtered = filtered.filter(cheque => {
                if (!cheque.cheque_date) return false;
                const chequeDate = new Date(cheque.cheque_date);
                chequeDate.setHours(0, 0, 0, 0);
                return chequeDate >= start;
            });
        }

        if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            filtered = filtered.filter(cheque => {
                if (!cheque.cheque_date) return false;
                const chequeDate = new Date(cheque.cheque_date);
                return chequeDate <= end;
            });
        }

        setFilteredCheques(filtered);
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setFilterBy('all');
        setChequeNoFilter('');
        setBankNameFilter('');
        setPayeeNameFilter('');
        setStatusFilter('');
        setCurrencyFilter('');
        setAddedByFilter('');
        setStartDate('');
        setEndDate('');
        setMinAmount('');
        setMaxAmount('');
        setSearchTerm('');
    };

    const handleSearch = () => {
        applyFilters();
    };

    // Helper function to convert hex to rgba
    const hexToRgba = (hex, alpha = 1) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    // PDF Export Function
    const exportToPDF = async () => {
        if (!filteredCheques || filteredCheques.length === 0) {
            showToast('warning', 'No data to export');
            return;
        }

        setPdfLoading(true);

        try {
            const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
            const primaryColor = currentTheme || '#8b5cf6';

            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4',
                compress: true
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            const rtlTextStyles = isRTL ? `
                unicode-bidi: plaintext;
                text-rendering: optimizeLegibility;
                font-kerning: normal;
                font-variant-ligatures: common-ligatures;
                font-feature-settings: "calt", "liga", "kern";
                letter-spacing: 0;
                word-spacing: normal;
            ` : '';

            const headerCellStyle = `
                padding: 10px 8px; 
                border: none; 
                text-align: center; 
                font-weight: 600; 
                font-size: 9px; 
                letter-spacing: ${isRTL ? '0' : '0.3px'};
                border-right: 1px solid rgba(255,255,255,0.1);
                ${rtlTextStyles}
            `;

            const addPatternBackground = (pdfDoc) => {
                pdfDoc.setFillColor(250, 251, 252);
                pdfDoc.rect(0, 0, pageWidth, pageHeight, 'F');

                const themeColor = hexToRgba(primaryColor, 0.5).match(/\d+/g);
                const r = parseInt(themeColor[0]);
                const g = parseInt(themeColor[1]);
                const b = parseInt(themeColor[2]);

                pdfDoc.setFillColor(r, g, b);
                pdfDoc.setDrawColor(r, g, b);
                pdfDoc.setLineWidth(0.2);

                const dotSize = 0.3;
                const dotSpacing = 8;

                for (let x = dotSpacing; x < pageWidth - dotSpacing; x += dotSpacing) {
                    for (let y = dotSpacing; y < pageHeight - dotSpacing; y += dotSpacing) {
                        pdfDoc.circle(x, y, dotSize, 'F');
                    }
                }

                pdfDoc.setFillColor(255, 255, 255);
                pdfDoc.setTextColor(0, 0, 0);
            };

            const rowsPerPage = 20;
            let chequesToExport = [...filteredCheques];
            const totalPagesCount = Math.ceil(chequesToExport.length / rowsPerPage);

            const headerHTML = `
                <div style="margin-bottom: 3px; padding-bottom: 5px; text-align: center; border-bottom: 2px solid ${hexToRgba(primaryColor, 0.05)}">
                    <div style="margin-bottom: 3px;">
                        <h1 style="color: ${primaryColor}; font-size: 22px; margin: 0 0 5px 0; font-weight: 800; letter-spacing: ${isRTL ? 'normal' : '-0.5px'}; ${rtlTextStyles}">
                            ${currentShop?.shop_name || translations.chequeList || 'Cheques List'}
                        </h1>
                        <div style="color: #666; font-size: 11px; font-weight: 500; margin-top: 3px; ${rtlTextStyles}">
                            ${translations.chequeReport || 'Cheque Report'} - ${new Date().toISOString().split('T')[0]}
                            ${currentShop ? ` | Shop: ${currentShop.name}` : ''}
                        </div>
                        <div style="color: #888; font-size: 9px; margin-top: 2px; ${rtlTextStyles}">
                            Generated by: ${user?.fullname || user?.username || 'System'}
                        </div>
                    </div>
                </div>
            `;

            const filterInfoHTML = (searchTerm || filterBy !== 'all' || startDate || endDate) ? `
                <div style="margin: 8px 0; padding: 6px; background: #f9fafb; border-radius: 6px; font-size: 8px; color: #6b7280; text-align: center;">
                    ${translations.reportFilters || 'Applied Filters'}: 
                    ${searchTerm ? `Search: ${searchTerm}` : ''}
                    ${statusFilter ? ` | Status: ${statusFilter}` : ''}
                    ${bankNameFilter ? ` | Bank: ${bankNameFilter}` : ''}
                    ${startDate ? ` | From: ${startDate}` : ''}
                    ${endDate ? ` | To: ${endDate}` : ''}
                    ${minAmount ? ` | Min: ${minAmount}` : ''}
                    ${maxAmount ? ` | Max: ${maxAmount}` : ''}
                </div>
            ` : '';

            const summaryStatsHTML = `
                <div style="display: flex; justify-content: center; gap: 20px; margin: 10px 0; flex-wrap: wrap;">
                    <div style="background: ${hexToRgba(primaryColor, 0.05)}; padding: 5px 15px; border-radius: 20px; border: 1px solid ${hexToRgba(primaryColor, 0.1)};">
                        <div style="font-size: 10px; color: #666; ${rtlTextStyles}">${translations.totalCheques || 'Total Cheques'}</div>
                        <div style="font-size: 14px; font-weight: 700; color: ${primaryColor};">${chequesToExport.length}</div>
                    </div>
                    <div style="background: ${hexToRgba(primaryColor, 0.05)}; padding: 5px 15px; border-radius: 20px; border: 1px solid ${hexToRgba(primaryColor, 0.1)};">
                        <div style="font-size: 10px; color: #666; ${rtlTextStyles}">${translations.totalAmount || 'Total Amount'}</div>
                        <div style="font-size: 14px; font-weight: 700; color: #059669;">$${chequesToExport.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0).toFixed(2)}</div>
                    </div>
                </div>
            `;

            const tableHeaderHTML = `
                <table style="width: 100%; border-collapse: separate; border-spacing: 0; margin-top: 5px; font-size: 8px; border-radius: 5px; overflow: hidden; border: 1px solid ${hexToRgba(primaryColor, 0.15)};">
                    <thead>
                        <tr style="background: linear-gradient(135deg, ${primaryColor}, ${hexToRgba(primaryColor, 0.9)}); color: white;">
                            <th style="${headerCellStyle}">${translations.serialNo || 'No.'}</th>
                            <th style="${headerCellStyle}">${translations.chequeNo || 'Cheque No'}</th>
                            <th style="${headerCellStyle}">${translations.bankName || 'Bank'}</th>
                            <th style="${headerCellStyle}">${translations.payeeName || 'Payee'}</th>
                            <th style="${headerCellStyle}">${translations.amount || 'Amount'}</th>
                            <th style="${headerCellStyle}">${translations.chequeDate || 'Cheque Date'}</th>
                            <th style="${headerCellStyle}">${translations.status || 'Status'}</th>
                            <th style="${headerCellStyle.replace('border-right:', 'border: none;')}">${translations.addedBy || 'Added By'}</th>
                        </tr>
                    </thead>
                    <tbody>
            `;

            for (let pageNum = 1; pageNum <= totalPagesCount; pageNum++) {
                const startRow = (pageNum - 1) * rowsPerPage;
                const endRow = Math.min(pageNum * rowsPerPage, chequesToExport.length);
                const pageCheques = chequesToExport.slice(startRow, endRow);

                const tableRowsHTML = pageCheques.map((cheque, index) => {
                    const statusStyle = (() => {
                        switch (cheque.status) {
                            case 'cleared': return { bg: '#10b98120', color: '#10b981' };
                            case 'bounced': return { bg: '#ef444420', color: '#ef4444' };
                            case 'pending': return { bg: '#f59e0b20', color: '#f59e0b' };
                            default: return { bg: '#6b728020', color: '#6b7280' };
                        }
                    })();

                    return `
                        <tr style="${index % 2 === 0 ? 'background-color: #fafafa;' : 'background-color: #ffffff;'}">
                            <td style="padding: 6px; border: none; text-align: center; font-weight: 600; color: ${primaryColor}; border-bottom: 1px solid #f0f0f0;">
                                <div style="display: inline-flex; align-items: center; justify-content: center; width: 20px; height: 20px; background: ${hexToRgba(primaryColor, 0.1)}; border-radius: 3px; font-size: 9px;">
                                    ${startRow + index + 1}
                                </div>
                            </td>
                            <td style="padding: 6px; border: none; text-align: center; font-weight: 500; color: #111827; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                <div style="font-weight: 600;">${cheque.cheque_no || '-'}</div>
                                <div style="font-size: 6px; color: #9ca3af;">ID: ${cheque.id}</div>
                            </td                            <td style="padding: 6px; border: none; text-align: center; color: #4b5563; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                ${cheque.bank_name || '-'}
                            </td>
                            <td style="padding: 6px; border: none; text-align: center; color: #4b5563; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                ${cheque.payee_name || '-'}
                            </td>
                            <td style="padding: 6px; border: none; text-align: center; font-weight: 600; color: #059669; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                ${cheque.currency_symbol || '$'} ${parseFloat(cheque.amount || 0).toFixed(2)}
                            </td>
                            <td style="padding: 6px; border: none; text-align: center; color: #6b7280; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                ${formatDate(cheque.cheque_date) || '-'}
                            </td>
                            <td style="padding: 6px; border: none; text-align: center; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                <div style="display: inline-block; padding: 2px 6px; border-radius: 10px; background: ${statusStyle.bg}; color: ${statusStyle.color}; font-weight: 500; font-size: 7px;">
                                    ${translations[cheque.status] || cheque.status || '-'}
                                </div>
                            </td>
                            <td style="padding: 6px; border: none; text-align: center; color: #4b5563; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                ${cheque.created_by_username || '-'}
                            </td>
                        </tr>
                    `;
                }).join('');

                const tableHTML = tableHeaderHTML + tableRowsHTML + '</tbody></table>';

                const pageHTML = `
                    <div style="width: 800px; padding: 15px 20px; background-color: rgba(255, 255, 255, 0.98); font-family: ${isRTL ? "'Noto Naskh Arabic', 'Segoe UI', Tahoma" : "'Inter', 'Segoe UI', sans-serif"}; direction: ${isRTL ? 'rtl' : 'ltr'}; text-align: ${isRTL ? 'right' : 'left'}; color: #1a1a1a; border-radius: 4px; border: 1px solid ${hexToRgba(primaryColor, 0.1)};">
                        ${headerHTML}
                        ${filterInfoHTML}
                        ${summaryStatsHTML}
                        <div style="overflow-x: auto;">
                            ${tableHTML}
                        </div>
                    </div>
                `;

                const pdfContainer = document.createElement('div');
                pdfContainer.style.position = 'absolute';
                pdfContainer.style.left = '-9999px';
                pdfContainer.style.top = '0';
                pdfContainer.style.width = '800px';
                pdfContainer.innerHTML = pageHTML;
                document.body.appendChild(pdfContainer);

                const canvas = await html2canvas(pdfContainer, {
                    scale: 1.5,
                    useCORS: true,
                    backgroundColor: '#ffffff',
                    logging: false,
                    onclone: (clonedDoc) => {
                        if (isRTL) {
                            const elements = clonedDoc.querySelectorAll('div, span, td, th, h1');
                            elements.forEach(el => {
                                el.style.unicodeBidi = 'plaintext';
                                el.style.textRendering = 'optimizeLegibility';
                                el.style.fontFeatureSettings = '"calt", "liga", "kern"';
                            });
                        }
                    }
                });

                document.body.removeChild(pdfContainer);

                const imgWidth = pageWidth - 20;
                const imgHeight = (canvas.height * imgWidth) / canvas.width;

                if (pageNum > 1) {
                    pdf.addPage();
                }

                addPatternBackground(pdf);
                pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, 10, imgWidth, Math.min(imgHeight, pageHeight - 20));

                pdf.setFontSize(8);
                pdf.setTextColor(120, 120, 120);
                pdf.text(`Page ${pageNum} of ${totalPagesCount}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
            }

            const fileName = `cheques_${currentShop?.shop_name?.replace(/[^a-z0-9]/gi, '_') || 'report'}_${new Date().toISOString().split('T')[0]}.pdf`;
            pdf.save(fileName);

            showToast('success', `PDF exported successfully with ${chequesToExport.length} cheque records across ${totalPagesCount} pages!`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            showToast('error', 'Error generating PDF. Please try again.');
        } finally {
            setPdfLoading(false);
        }
    };

    const handleView = (cheque) => {
        setSelectedCheque(cheque);
        setShowViewModal(true);
    };

    const handleEdit = (cheque) => {
        if (cheque.created_by_username !== user?.username) {
            showToast('error', 'You can only edit your own cheques');
            return;
        }

        setSelectedCheque(cheque);
        setEditFormData({
            cheque_no: cheque.cheque_no || '',
            bank_name: cheque.bank_name || '',
            payee_name: cheque.payee_name || '',
            amount: cheque.amount || '',
            currency_code: cheque.currency_code || 'USD',
            currency_symbol: cheque.currency_symbol || '$',
            issue_date: cheque.issue_date || '',
            cheque_date: cheque.cheque_date || '',
            deposit_date: cheque.deposit_date || '',
            status: cheque.status || '',
            remarks: cheque.remarks || '',
            bank_branch: cheque.bank_branch || '',
            bank_code: cheque.bank_code || '',
            account_number: cheque.account_number || '',
            drawer_name: cheque.drawer_name || '',
            drawer_phone: cheque.drawer_phone || '',
            bounce_reason: cheque.bounce_reason || '',
            bounce_date: cheque.bounce_date || ''
        });
        setEditFormErrors({});
        setShowEditModal(true);
    };

    const handleDeleteClick = (chequeId) => {
        const cheque = cheques.find(c => c.id === chequeId);
        if (!cheque || cheque.created_by_username !== user?.username) {
            showToast('error', 'You can only delete your own cheques');
            return;
        }

        setChequeToDelete(chequeId);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!chequeToDelete || !currentShop) return;

        const cheque = cheques.find(c => c.id === chequeToDelete);
        if (!cheque || cheque.created_by_username !== user?.username) {
            showToast('error', 'You can only delete your own cheques');
            setShowDeleteModal(false);
            setChequeToDelete(null);
            return;
        }

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/cheques.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'delete',
                    id: chequeToDelete,
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations.deletedSuccess || 'Cheque deleted successfully');
                fetchCheques();
                setShowDeleteModal(false);
                setChequeToDelete(null);
            } else {
                showToast('error', data.message || translations.deleteError || 'Error deleting cheque');
            }
        } catch (error) {
            console.error('Error deleting cheque:', error);
            showToast('error', translations.deleteError || 'Error deleting cheque');
        }
    };

    const handleUpdateCheque = async (e) => {
        e.preventDefault();

        if (!selectedCheque || !currentShop) return;

        const errors = {};
        if (!editFormData.cheque_no.trim()) {
            errors.cheque_no = translations.chequeNoRequired || 'Cheque number is required';
        }

        if (Object.keys(errors).length > 0) {
            setEditFormErrors(errors);
            return;
        }

        setEditLoading(true);

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/cheques.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'edit',
                    id: selectedCheque.id,
                    username: user.username,
                    shop_id: currentShop.shop_id,
                    ...editFormData
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations.chequeUpdated || 'Cheque updated successfully');
                setShowEditModal(false);
                fetchCheques();
            } else {
                showToast('error', data.message || translations.updateError || 'Error updating cheque');
            }
        } catch (error) {
            console.error('Error updating cheque:', error);
            showToast('error', translations.updateError || 'Error updating cheque');
        } finally {
            setEditLoading(false);
        }
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return dateString;
            }
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (error) {
            return dateString;
        }
    };

    // Get today's date
    const getTodayDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    // Set max date for end date based on start date
    useEffect(() => {
        if (startDate && endDateRef.current) {
            endDateRef.current.min = startDate;
        }
    }, [startDate]);

    // Set max date for start date to today
    useEffect(() => {
        const today = getTodayDate();
        if (startDateRef.current) {
            startDateRef.current.max = today;
        }
        if (endDateRef.current) {
            endDateRef.current.max = today;
        }
    }, []);

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`customer-list-modern ${isRTL ? 'rtl' : ''}`}>
            {/* Toast Notification */}
            <ToastNotification
                toast={toast}
                setToast={setToast}
                translations={translations}
            />

            {/* Shop Selector Modal */}
            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />

            {/* Header Section */}
            {/* <div className="list-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faMoneyCheckAlt} />
                    </div>
                    <div className="header-text">
                        <h1>{translations.chequeList || 'Cheques List'}</h1>
                        <div className="shop-info">
                            <p>
                                {currentShop?.shop_name
                                    ? `${translations.shopName || 'Shop'}: ${currentShop.shop_name}`
                                    : `${translations.allCheques || 'My Cheques'} - ${user?.username}`}
                            </p>
                        </div>
                    </div>
                </div>

                {renderShopIndicator()}
            </div> */}

            {/* Cashable Reminder Component */}
            {currentShop && filteredCheques.length > 0 && (
                <CashableReminder cheques={filteredCheques} translations={translations} />
            )}

            {/* Advanced Filters Section */}
            <div className="advanced-filters-section">
                <div className="filters-row">
                    <div className="filter-group compact">
                        <select
                            value={filterBy}
                            onChange={(e) => setFilterBy(e.target.value)}
                            className="filter-select"
                            dir={isRTL ? 'rtl' : 'ltr'}
                        >
                            <option value="all">{translations.all || 'All'}</option>
                            <option value="chequeNo">{translations.chequeNoFilter || 'Cheque Number'}</option>
                            <option value="bankName">{translations.bankNameFilter || 'Bank Name'}</option>
                            <option value="payeeName">{translations.payeeNameFilter || 'Payee Name'}</option>
                            <option value="status">{translations.statusFilter || 'Status'}</option>
                            <option value="currency">{translations.currency || 'Currency'}</option>
                            <option value="date">{translations.dateRange || 'Date Range'}</option>
                            <option value="amount">{translations.amount || 'Amount Range'}</option>
                            <option value="added_by">{translations.addedByFilter || 'Added By'}</option>
                        </select>
                    </div>

                    {filterBy === 'chequeNo' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={chequeNoFilter}
                                onChange={(e) => setChequeNoFilter(e.target.value)}
                                placeholder={translations.chequeNoFilter || 'Cheque number...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'bankName' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={bankNameFilter}
                                onChange={(e) => setBankNameFilter(e.target.value)}
                                placeholder={translations.bankNameFilter || 'Bank name...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'payeeName' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={payeeNameFilter}
                                onChange={(e) => setPayeeNameFilter(e.target.value)}
                                placeholder={translations.payeeNameFilter || 'Payee name...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'status' && (
                        <div className="filter-group compact">
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="filter-select"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            >
                                {statusOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {filterBy === 'currency' && (
                        <div className="filter-group compact">
                            <select
                                value={currencyFilter}
                                onChange={(e) => setCurrencyFilter(e.target.value)}
                                className="filter-select"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            >
                                {currencyOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {filterBy === 'amount' && (
                        <>
                            <div className="filter-group compact">
                                <input
                                    type="number"
                                    value={minAmount}
                                    onChange={(e) => setMinAmount(e.target.value)}
                                    placeholder={translations.minAmount || 'Min'}
                                    className="filter-input compact"
                                    step="0.01"
                                    min="0"
                                />
                            </div>
                            <div className="filter-group compact">
                                <input
                                    type="number"
                                    value={maxAmount}
                                    onChange={(e) => setMaxAmount(e.target.value)}
                                    placeholder={translations.maxAmount || 'Max'}
                                    className="filter-input compact"
                                    step="0.01"
                                    min="0"
                                />
                            </div>
                        </>
                    )}

                    {filterBy === 'date' && (
                        <>
                            <div className="filter-group compact date-filter">
                                <input
                                    ref={startDateRef}
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    max={getTodayDate()}
                                    className="filter-input compact date-input"
                                    title={translations.startDate || 'Start Date'}
                                />
                            </div>
                            <div className="filter-group compact date-filter">
                                <input
                                    ref={endDateRef}
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    min={startDate}
                                    max={getTodayDate()}
                                    className="filter-input compact date-input"
                                    title={translations.endDate || 'End Date'}
                                />
                            </div>
                        </>
                    )}

                    {filterBy === 'added_by' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={addedByFilter}
                                onChange={(e) => setAddedByFilter(e.target.value)}
                                placeholder={translations.addedByFilter || 'Added by username...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    <div className="filter-group compact search-group">
                        <div className="search-box compact">
                            <input
                                type="text"
                                placeholder={translations.searchPlaceholder || 'Search cheques...'}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    </div>

                    <div className="filter-group compact">
                        <button
                            onClick={fetchCheques}
                            className="refresh-btn-modern compact"
                            disabled={loading}
                            title={translations.refresh || 'Refresh'}
                        >
                            <FontAwesomeIcon icon={faRefresh} />
                            <span className="btn-text">{translations.refresh || 'Refresh'}</span>
                        </button>
                    </div>

                    <div className="filter-group compact">
                        <button
                            onClick={exportToPDF}
                            className="refresh-btn-modern compact"
                            disabled={pdfLoading || filteredCheques.length === 0}
                            title={translations.exportToPDF || 'Export PDF'}
                            style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)' }}
                        >
                            {pdfLoading ? (
                                <div className="btn-spinner-small"></div>
                            ) : (
                                <FontAwesomeIcon icon={faFilePdf} />
                            )}
                            <span className="btn-text">{translations.exportToPDF || 'Export PDF'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Loading State */}
            {loading ? (
                <div className="loading-container">
                    <div className="loading-spinner-modern"></div>
                    <p>{translations.loadingUserData || 'Loading your cheque data...'}</p>
                </div>
            ) : !currentShop ? (
                <div className="no-shop-container">
                    <div className="no-shop-message">
                        <FontAwesomeIcon icon={faStore} />
                        <h3>{translations.noShopSelected || 'No Shop Selected'}</h3>
                        <p>{translations.selectShop || 'Please select a shop to view cheques'}</p>
                        <button
                            className="btn-modern btn-primary-modern"
                            onClick={() => setShowShopSelector(true)}
                        >
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>{translations.switchShop || 'Switch Shop'}</span>
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    {/* Table Section */}
                    <div className="table-scroll-container">
                        <div className="table-container-modern">
                            <div className="customer-table-wrapper">
                                <table className="customer-table-modern">
                                    <thead>
                                        <tr>
                                            <th className="id-column centered-cell">{translations.serialNo || 'S.No'}</th>
                                            <th className="centered-cell">{translations.chequeNo || 'Cheque No'}</th>
                                            <th className="centered-cell">{translations.bankName || 'Bank'}</th>
                                            <th className="centered-cell">{translations.payeeName || 'Payee'}</th>
                                            <th className="centered-cell">{translations.amount || 'Amount'}</th>
                                            <th className="centered-cell">{translations.issueDate || 'Issue'}</th>
                                            <th className="centered-cell">{translations.chequeDate || 'Cheque'}</th>
                                            <th className="centered-cell">{translations.depositDate || 'Deposit'}</th>
                                            <th className="centered-cell">{translations.status || 'Status'}</th>
                                            <th className="centered-cell">{translations.remarks || 'Remarks'}</th>
                                            <th className="centered-cell">{translations.addedBy || 'Added By'}</th>
                                            <th className="actions-column centered-cell">{translations.actions || 'Actions'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentCheques.map((cheque, index) => {
                                            const addedBy = cheque.created_by_username || '-';
                                            const isSelfAdded = addedBy === user?.username;
                                            
                                            const isCashableSoon = (() => {
                                                if (!cheque.cheque_date) return false;
                                                const status = cheque.status;
                                                if (status === 'cleared' || status === 'bounced' || status === 'cancelled' || status === 'stopped') {
                                                    return false;
                                                }
                                                const today = new Date();
                                                today.setHours(0, 0, 0, 0);
                                                const chequeDate = new Date(cheque.cheque_date);
                                                chequeDate.setHours(0, 0, 0, 0);
                                                const sevenDaysFromNow = new Date(today);
                                                sevenDaysFromNow.setDate(today.getDate() + 7);
                                                return chequeDate >= today && chequeDate <= sevenDaysFromNow;
                                            })();

                                            return (
                                                <tr key={cheque.id} style={isCashableSoon ? { backgroundColor: '#fef3c720' } : {}}>
                                                    <td className="id-cell centered-cell">
                                                        <span className="cell-text">{startIndex + index + 1}</span>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <span className="cell-text cheque-number" style={isCashableSoon ? { color: '#f59e0b', fontWeight: 'bold' } : {}}>
                                                            {cheque.cheque_no || '-'}
                                                            {isCashableSoon && <FontAwesomeIcon icon={faBell} style={{ marginLeft: '5px', fontSize: '10px', color: '#f59e0b' }} />}
                                                        </span>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <span className="cell-text">{cheque.bank_name || '-'}</span>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <span className="cell-text">{cheque.payee_name || '-'}</span>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <span className="cell-text amount" style={isCashableSoon ? { fontWeight: 'bold' } : {}}>
                                                            {cheque.currency_symbol || '$'} {parseFloat(cheque.amount || 0).toFixed(2)}
                                                        </span>
                                                        <small className="currency-code">{cheque.currency_code}</small>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <span className="cell-text">{formatDate(cheque.issue_date)}</span>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <span className="cell-text" style={isCashableSoon ? { color: '#f59e0b', fontWeight: 'bold' } : {}}>
                                                            {formatDate(cheque.cheque_date)}
                                                        </span>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <span className="cell-text">{formatDate(cheque.deposit_date) || '-'}</span>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <StatusBadge status={cheque.status} translations={translations} />
                                                    </td>
                                                    <td className="centered-cell">
                                                        <span className="cell-text remarks-cell" title={cheque.remarks}>
                                                            {cheque.remarks?.substring(0, 15) || '-'}
                                                            {cheque.remarks?.length > 15 && '...'}
                                                        </span>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <span className={`added-by-badge ${isSelfAdded ? 'self' : ''}`}>
                                                            <span className="cell-text">{addedBy}</span>
                                                        </span>
                                                    </td>
                                                    <td className="actions-cell-modern centered-cell">
                                                        <div className="actions-content">
                                                            <button
                                                                onClick={() => handleView(cheque)}
                                                                className="action-btn-modern view-btn-modern"
                                                                title={translations.view || 'View'}
                                                            >
                                                                <FontAwesomeIcon icon={faEye} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleEdit(cheque)}
                                                                className="action-btn-modern edit-btn-modern"
                                                                title={translations.edit || 'Edit'}
                                                                disabled={cheque.created_by_username !== user?.username}
                                                            >
                                                                <FontAwesomeIcon icon={faEdit} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteClick(cheque.id)}
                                                                className="action-btn-modern delete-btn-modern"
                                                                title={translations.delete || 'Delete'}
                                                                disabled={cheque.created_by_username !== user?.username}
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Pagination */}
                    {filteredCheques.length > 0 && currentShop && (
                        <div className="pagination-modern">
                            <div className="pagination-info">
                                <span>{translations.showing || 'Showing'} {startIndex + 1}-{Math.min(endIndex, filteredCheques.length)} {translations.of || 'of'} {filteredCheques.length} {translations.cheques || 'cheques'}</span>
                                {currentShop?.shop_name && (
                                    <span className="shop-info-pagination">
                                        | {translations.shopName || 'Shop'}: {currentShop.shop_name}
                                    </span>
                                )}
                            </div>
                            <div className="pagination-controls">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="pagination-btn"
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                    <span>{translations.previous || 'Previous'}</span>
                                </button>

                                <div className="page-numbers">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="pagination-btn"
                                >
                                    <span>{translations.next || 'Next'}</span>
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* View Modal - Does NOT close on outside click */}
            {showViewModal && selectedCheque && (
                <div className="modal-overlay-modern" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-content-modern view-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faMoneyCheckAlt} />
                            </div>
                            <div className="modal-title">
                                <h3>{translations.chequeDetails || 'Cheque Details'}</h3>
                                <span className="cheque-number-badge">#{selectedCheque.cheque_no}</span>
                            </div>
                            <button className="close-btn-modern" onClick={() => setShowViewModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body-modern view-modal-body">
                            <div className="view-section">
                                <h4 className="view-section-title"><FontAwesomeIcon icon={faMoneyCheck} />Cheque Summary</h4>
                                <div className="view-grid">
                                    <div className="view-row"><span className="view-label">{translations.chequeId || 'Cheque ID'}:</span><span className="view-value">#{selectedCheque.id}</span></div>
                                    <div className="view-row"><span className="view-label">{translations.chequeNo || 'Cheque Number'}:</span><span className="view-value">{selectedCheque.cheque_no || '-'}</span></div>
                                    <div className="view-row"><span className="view-label">{translations.bankName || 'Bank Name'}:</span><span className="view-value">{selectedCheque.bank_name || '-'}</span></div>
                                    <div className="view-row"><span className="view-label">{translations.payeeName || 'Payee Name'}:</span><span className="view-value">{selectedCheque.payee_name || '-'}</span></div>
                                    <div className="view-row"><span className="view-label">{translations.amount || 'Amount'}:</span><span className="view-value total-amount">{selectedCheque.currency_symbol || '$'} {parseFloat(selectedCheque.amount || 0).toFixed(2)}</span></div>
                                    <div className="view-row"><span className="view-label">{translations.status || 'Status'}:</span><span className="view-value"><StatusBadge status={selectedCheque.status} translations={translations} /></span></div>
                                </div>
                            </div>

                            <div className="view-section">
                                <h4 className="view-section-title"><FontAwesomeIcon icon={faClock} />{translations.timelineInfo || 'Timeline Information'}</h4>
                                <div className="view-grid">
                                    <div className="view-row"><span className="view-label">{translations.issueDate || 'Issue Date'}:</span><span className="view-value">{formatDate(selectedCheque.issue_date) || '-'}</span></div>
                                    <div className="view-row"><span className="view-label">{translations.chequeDate || 'Cheque Date'}:</span><span className="view-value">{formatDate(selectedCheque.cheque_date) || '-'}</span></div>
                                    {selectedCheque.deposit_date && (<div className="view-row"><span className="view-label">{translations.depositDate || 'Deposit Date'}:</span><span className="view-value">{formatDate(selectedCheque.deposit_date)}</span></div>)}
                                </div>
                            </div>

                            {selectedCheque.remarks && (
                                <div className="view-section">
                                    <h4 className="view-section-title"><FontAwesomeIcon icon={faComment} />{translations.remarks || 'Remarks'}</h4>
                                    <div className="remarks-box">{selectedCheque.remarks}</div>
                                </div>
                            )}

                            <div className="view-section metadata-section">
                                <div className="view-grid">
                                    <div className="view-row"><span className="view-label">{translations.addedBy || 'Added By'}:</span><span className="view-value"><span className="added-by-badge">{selectedCheque.created_by_username || '-'}</span></span></div>
                                    <div className="view-row"><span className="view-label">{translations.dateAdded || 'Date Added'}:</span><span className="view-value">{formatDate(selectedCheque.created_at) || formatDate(selectedCheque.issue_date)}</span></div>
                                    {selectedCheque.shop_name && (<div className="view-row"><span className="view-label">{translations.shopInfo || 'Shop'}:</span><span className="view-value">{selectedCheque.shop_name}</span></div>)}
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button className="btn-modern btn-secondary-modern" onClick={() => setShowViewModal(false)}>{translations.close || 'Close'}</button>
                            {selectedCheque.created_by_username === user?.username && (
                                <button className="btn-modern btn-primary-modern" onClick={() => { setShowViewModal(false); handleEdit(selectedCheque); }}>
                                    <FontAwesomeIcon icon={faEdit} />
                                    {translations.edit || 'Edit'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal - MOVABLE, RESIZABLE, Does NOT close on outside click */}
            {showEditModal && selectedCheque && (
                <div className="modal-overlay-modern" onClick={(e) => e.stopPropagation()}>
                    <div 
                        ref={modalRef}
                        className="modal-content-modern edit-modal-content" 
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            position: 'fixed',
                            left: modalPosition.x || '50%',
                            top: modalPosition.y || '50%',
                            transform: modalPosition.x === 0 && modalPosition.y === 0 ? 'translate(-50%, -50%)' : 'none',
                            width: modalSize.width,
                            minWidth: '400px',
                            maxWidth: '95vw',
                            maxHeight: '85vh',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        {/* Resize Handles */}
                        <div className="resize-handle-top" onMouseDown={(e) => startResize(e, 'n')}></div>
                        <div className="resize-handle-bottom" onMouseDown={(e) => startResize(e, 's')}></div>
                        <div className="resize-handle-left" onMouseDown={(e) => startResize(e, 'w')}></div>
                        <div className="resize-handle-right" onMouseDown={(e) => startResize(e, 'e')}></div>
                        <div className="resize-handle-nw" onMouseDown={(e) => startResize(e, 'nw')}></div>
                        <div className="resize-handle-ne" onMouseDown={(e) => startResize(e, 'ne')}></div>
                        <div className="resize-handle-sw" onMouseDown={(e) => startResize(e, 'sw')}></div>
                        <div className="resize-handle-se" onMouseDown={(e) => startResize(e, 'se')}></div>

                        {/* Draggable Header */}
                        <div className="modal-header-modern" onMouseDown={startDrag} style={{ cursor: 'move', flexShrink: 0 }}>
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faEdit} />
                            </div>
                            <div className="modal-title">
                                <h3>{translations.editCheque || 'Edit Cheque'}</h3>
                                <span className="cheque-number-badge">#{selectedCheque.cheque_no}</span>
                            </div>
                            
                            {/* Width Control Buttons */}
                            <div className="width-control-group">
                                {Object.entries(widthModes).map(([key, mode]) => (
                                    <button
                                        key={key}
                                        className={`width-btn ${currentWidthMode === key ? 'active' : ''}`}
                                        onClick={() => setWidthMode(key)}
                                        title={mode.label}
                                        type="button"
                                    >
                                        <FontAwesomeIcon icon={mode.icon} />
                                    </button>
                                ))}
                            </div>
                            
                            <button className="close-btn-modern" onClick={() => setShowEditModal(false)} type="button">
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <form onSubmit={handleUpdateCheque} style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                            <div className="modal-body-modern" style={{ flex: 1, overflow: 'auto' }}>
                                <div className="form-grid edit-form-grid">
                                    <div className={`form-group ${editFormErrors.cheque_no ? 'has-error' : ''}`}>
                                        <label htmlFor="cheque_no">{translations.chequeNo || 'Cheque Number'} <span className="required">*</span></label>
                                        <input
                                            type="text"
                                            id="cheque_no"
                                            name="cheque_no"
                                            value={editFormData.cheque_no}
                                            onChange={(e) => setEditFormData({...editFormData, cheque_no: e.target.value})}
                                            className={editFormErrors.cheque_no ? 'error' : ''}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                        {editFormErrors.cheque_no && (<div className="error-message"><FontAwesomeIcon icon={faExclamationTriangle} />{editFormErrors.cheque_no}</div>)}
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="bank_name">{translations.bankName || 'Bank Name'}</label>
                                        <input
                                            type="text"
                                            id="bank_name"
                                            name="bank_name"
                                            value={editFormData.bank_name}
                                            onChange={(e) => setEditFormData({...editFormData, bank_name: e.target.value})}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="payee_name">{translations.payeeName || 'Payee Name'}</label>
                                        <input
                                            type="text"
                                            id="payee_name"
                                            name="payee_name"
                                            value={editFormData.payee_name}
                                            onChange={(e) => setEditFormData({...editFormData, payee_name: e.target.value})}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="amount">{translations.amount || 'Amount'}</label>
                                        <input
                                            type="number"
                                            id="amount"
                                            name="amount"
                                            value={editFormData.amount}
                                            onChange={(e) => setEditFormData({...editFormData, amount: e.target.value})}
                                            step="0.01"
                                            min="0"
                                            dir="ltr"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="currency_code">{translations.currency || 'Currency'}</label>
                                        <select
                                            id="currency_code"
                                            name="currency_code"
                                            value={editFormData.currency_code}
                                            onChange={(e) => {
                                                const currencyMap = {
                                                    'USD': '$', 'EUR': '€', 'GBP': '£', 'JPY': '¥', 'CNY': '¥',
                                                    'INR': '₹', 'PKR': '₨', 'AED': 'د.إ', 'SAR': '﷼', 'CAD': 'C$',
                                                    'AUD': 'A$', 'CHF': 'Fr', 'SGD': 'S$'
                                                };
                                                setEditFormData({
                                                    ...editFormData,
                                                    currency_code: e.target.value,
                                                    currency_symbol: currencyMap[e.target.value] || '$'
                                                });
                                            }}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        >
                                            {currencyOptions.filter(opt => opt.value).map(option => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="issue_date">{translations.issueDate || 'Issue Date'}</label>
                                        <input
                                            type="date"
                                            id="issue_date"
                                            name="issue_date"
                                            value={editFormData.issue_date}
                                            onChange={(e) => setEditFormData({...editFormData, issue_date: e.target.value})}
                                            max={getTodayDate()}
                                            dir="ltr"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="cheque_date">{translations.chequeDate || 'Cheque Date'}</label>
                                        <input
                                            type="date"
                                            id="cheque_date"
                                            name="cheque_date"
                                            value={editFormData.cheque_date}
                                            onChange={(e) => setEditFormData({...editFormData, cheque_date: e.target.value})}
                                            max={getTodayDate()}
                                            dir="ltr"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="deposit_date">{translations.depositDate || 'Deposit Date'}</label>
                                        <input
                                            type="date"
                                            id="deposit_date"
                                            name="deposit_date"
                                            value={editFormData.deposit_date}
                                            onChange={(e) => setEditFormData({...editFormData, deposit_date: e.target.value})}
                                            min={editFormData.cheque_date}
                                            max={getTodayDate()}
                                            dir="ltr"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="status">{translations.status || 'Status'}</label>
                                        <select
                                            id="status"
                                            name="status"
                                            value={editFormData.status}
                                            onChange={(e) => setEditFormData({...editFormData, status: e.target.value})}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        >
                                            {statusOptions.filter(opt => opt.value).map(option => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group full-width">
                                        <label htmlFor="remarks">{translations.remarks || 'Remarks'}</label>
                                        <textarea
                                            id="remarks"
                                            name="remarks"
                                            value={editFormData.remarks}
                                            onChange={(e) => setEditFormData({...editFormData, remarks: e.target.value})}
                                            rows="3"
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                    </div>
                                </div>

                                {/* Cheque Info Footer */}
                                <div className="edit-cheque-info">
                                    <p><strong>{translations.chequeId || 'Cheque ID'}:</strong> #{selectedCheque.id}</p>
                                    <p><strong>{translations.addedBy || 'Added By'}:</strong> {selectedCheque.created_by_username}</p>
                                    <p><strong>{translations.shopName || 'Shop'}:</strong> {currentShop?.shop_name || 'N/A'}</p>
                                </div>
                            </div>

                            <div className="modal-footer-modern" style={{ flexShrink: 0 }}>
                                <button type="button" className="btn-modern btn-secondary-modern" onClick={() => setShowEditModal(false)} disabled={editLoading}>
                                    <FontAwesomeIcon icon={faTimes} />
                                    {translations.cancel || 'Cancel'}
                                </button>
                                <button type="submit" className="btn-modern btn-primary-modern" disabled={editLoading}>
                                    {editLoading ? (
                                        <><div className="btn-spinner"></div><span>{translations.updating || 'Updating...'}</span></>
                                    ) : (
                                        <><FontAwesomeIcon icon={faSave} /><span>{translations.saveChanges || 'Save Changes'}</span></>
                                    )}
                                </button>
                            </div>
                        </form>

                        {editLoading && (
                            <div className="loading-overlay edit-loading-overlay">
                                <div className="loading-content">
                                    <div className="loading-spinner"></div>
                                    <p>{translations.updating || 'Updating...'}</p>
                                    <p className="loading-subtext">Please wait while we update cheque data</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal - Does NOT close on outside click */}
            {showDeleteModal && (
                <div className="modal-overlay-modern" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-content-modern delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern delete-header">
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                            </div>
                            <div className="modal-title">
                                <h3 style={{ color: 'var(--customer-error-color)' }}>
                                    {translations.confirmDelete || 'Confirm Delete'}
                                </h3>
                            </div>
                            <button className="close-btn-modern" onClick={() => setShowDeleteModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="delete-message">
                                <p style={{ fontSize: '1.1rem', marginBottom: '10px', color: 'var(--customer-text-primary)' }}>
                                    {translations.deleteMessage || 'Are you sure you want to delete this cheque?'}
                                </p>
                                <p style={{ fontSize: '0.9rem', color: 'var(--customer-warning-color)' }}>
                                    <FontAwesomeIcon icon={faExclamationTriangle} />{' '}
                                    {translations.deleteWarning || 'This action cannot be undone.'}
                                </p>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button type="button" className="btn-modern btn-secondary-modern" onClick={() => setShowDeleteModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                                {translations.noCancel || 'No, Cancel'}
                            </button>
                            <button type="button" className="btn-modern btn-delete-modern" onClick={handleDelete}>
                                <FontAwesomeIcon icon={faTrash} />
                                {translations.yesDelete || 'Yes, Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChequeList;